import { VocabularyService } from './../vocabularies/vocabulary.service';
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { OrderService } from './order.service';
import { TranslationService } from '../services/translation.service';
import { WorkspaceFilterService } from '../services/workspace-filter.service';

import {
    BaseFacet,
    BaseFacetService,
    IFacet
} from '../common/facet';

import { OrderTableOptions } from './order-table-options';
import { OrderFilterComponent } from './order-filter.component';

import {
    CellFormatterService,
    TableState,
    DataResponse,
    ColumnsState,
    TableColumnDef,
} from '@common/datatable';
import { QueryDef } from '../services/query-def';
import { filterToDate } from '../common/util';
import { FeatureFlagService } from '../services/feature-flags.service';
import { Entity, Order } from '../common/types';
import { DataContextService } from '@services/data-context.service';
import { arrowClockwise, magnifier } from '@icons';

@Component({
    selector: 'order-facet',
    templateUrl: './order-facet.component.html',
    providers: BaseFacet.BASE_COMPONENT_PROVIDERS
})
export class OrderFacetComponent extends BaseFacet<Entity<Order>> implements OnInit, OnDestroy {
    @Input() facetId: string;
    @Input() facet: IFacet;

    readonly icons = { arrowClockwise, magnifier };

    componentName = 'orders';

    orderTableOptions: OrderTableOptions;

    readonly COMPONENT_LOG_TAG = 'order-facet';

    private notifier$ = new Subject<void>();

    dataTableColumns: BehaviorSubject<TableColumnDef[]>;
    dataTableColumns$: Observable<TableColumnDef[]>;

    constructor(
        private baseFacetService: BaseFacetService,
        private cellFormatterService: CellFormatterService,
        private orderService: OrderService,
        private modalService: NgbModal,
        private translationService: TranslationService,
        workspaceFilterService: WorkspaceFilterService,
        private vocabularyService: VocabularyService,
        private featureFlagService: FeatureFlagService,
        private dataContext: DataContextService,
    ) {
        super(
            baseFacetService,
            workspaceFilterService
        );

        this.orderTableOptions = new OrderTableOptions(
            this.cellFormatterService,
            this.translationService,
            this.featureFlagService
        );

        this.dataTableColumns = new BehaviorSubject(this.orderTableOptions.options.columns);
        this.dataTableColumns$ = this.dataTableColumns.asObservable();

        this.dataService = {
            run: (tableState: TableState) => {
                return this.loadItemsList(tableState);
            }
        };
    }

    // lifecycle
    ngOnInit() {
        super.ngOnInit();
        this.initialize();

        this.dataContext.onCancel$.pipe(takeUntil(this.notifier$)).subscribe(() => {
            this.changeView(this.LIST_VIEW);
        });
    }

    initialize() {
        this.restoreFilterState();
        this.changeView(this.LIST_VIEW);
    }

    refreshData() {
        this.initialize();
        this.dataTableComm.reloadTable();
    }

    restoreFilterState() {
        // process any grid filters
        if (this.facet && this.facet.GridFilter) {
            try {
                this.filter = JSON.parse(this.facet.GridFilter);
            } catch (err) {
                console.error(err);
            }

            if (this.filter) {
                this.filter.DateReceivedStart = filterToDate(this.filter.DateReceivedStart);
                this.filter.DateReceivedEnd = filterToDate(this.filter.DateReceivedEnd);
                this.filter.DateProjectedReceiptStart = filterToDate(this.filter.DateProjectedReceiptStart);
                this.filter.DateProjectedReceiptEnd = filterToDate(this.filter.DateProjectedReceiptEnd);
            } else {
                this.filter = {};
            } 
        }
    }

    async loadItemsList(tableState: TableState): Promise<DataResponse> {
        this.tableState = tableState;

        this.setLoadingState(tableState.loadingMessage);

        const page = tableState.pageNumber || 0;
        const pageSize = tableState.pageSize || 50;
        const sort = tableState.sort || 'DateCreated DESC';
        const expands: string[] = [];

        const queryDef: QueryDef = {
            page,
            size: pageSize,
            sort,
            filter: this.getActiveFilter(),
            expands
        };

        try {
            const response = await this.orderService.getOrders(queryDef);

            const visibleColumns = this.getVisibleColumns(this.orderTableOptions.options);
            await this.orderService.ensureVisibleColumnsDataLoaded(response.results, visibleColumns);

            this.stopLoading();

            this.data = response.results as Entity<Order>[];
            this.totalCount = response.inlineCount;
            this.updatePageState();

            return {
                results: this.data,
                inlineCount: this.totalCount
            };
        } finally {
            this.stopLoading();
        }
    }

    addItemClick() {
        this.setLoadingState();
        this.createNewItem().then((item) => {
            this.stopLoading();
            this.itemToEdit = item;
            this.changeView(this.DETAIL_VIEW);
        }).catch((error) => {
            this.loading = false;
            this.loggingService.logError("An unexpected error occurred. Please try again", error, this.componentName, true);
        });
    }

    createNewItem(): Promise<any> {
        const newOrder = this.orderService.createOrder();
        return Promise.all([]).then(() => {
            return newOrder;
        });
    }

    openFilter() {
        const ref = this.modalService.open(OrderFilterComponent, { size: 'lg' });
        const component = ref.componentInstance as OrderFilterComponent;
        component.filter = this.filter;
        component.onFilter.subscribe((filter: any) => {
            this.filter = filter;
            this.runFilter();
        });
    }

    async selectedColumnsChange({ visible }: ColumnsState) {
        try {
            this.facetLoadingState.changeLoadingState(true);
            await this.orderService.ensureVisibleColumnsDataLoaded(this.data, visible);
        } finally {
            this.facetLoadingState.changeLoadingState(false);
        }
    }

    ngOnDestroy(): void {
        this.notifier$.next();
        this.notifier$.complete();
    }
}
